import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../contexts/ThemeContext';

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  state: string;
  purchase_price: string;
  broker: string;
  last_contact: string;
  client_state: string;
  date_created: string;
  user_id: string;
  loan_type: string;
  lead_source: string;
  lender_name: string;
  loan_amount?: string;
  client_state_updated?: string;
  realtor_id?: string;
  closing_date?: string;
  comp?: string;
  interest_rate?: string;
  notes?: string;
}

interface Realtor {
  id: string;
  name: string;
  email: string;
  phone: string;
  state: string;
}

interface CompRate {
  id: string;
  broker: string;
  comp_percentage: number;
  personal_comp_percentage: number;
  full_broker_name: string;
}

const parsePurchasePrice = (priceString: string): number => {
  const cleaned = priceString.replace(/[$,]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)})-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const formatPurchasePrice = (price: string): string => {
  const cleaned = price.replace(/[$,]/g, '');
  const parsed = parseFloat(cleaned);
  if (isNaN(parsed)) return price;
  return `$${parsed.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const getStateAbbreviation = (state: string): string => {
  const stateMap: Record<string, string> = {
    'ALABAMA': 'AL', 'ALASKA': 'AK', 'ARIZONA': 'AZ', 'ARKANSAS': 'AR', 'CALIFORNIA': 'CA',
    'COLORADO': 'CO', 'CONNECTICUT': 'CT', 'DELAWARE': 'DE', 'FLORIDA': 'FL', 'GEORGIA': 'GA',
    'HAWAII': 'HI', 'IDAHO': 'ID', 'ILLINOIS': 'IL', 'INDIANA': 'IN', 'IOWA': 'IA',
    'KANSAS': 'KS', 'KENTUCKY': 'KY', 'LOUISIANA': 'LA', 'MAINE': 'ME', 'MARYLAND': 'MD',
    'MASSACHUSETTS': 'MA', 'MICHIGAN': 'MI', 'MINNESOTA': 'MN', 'MISSISSIPPI': 'MS', 'MISSOURI': 'MO',
    'MONTANA': 'MT', 'NEBRASKA': 'NE', 'NEVADA': 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ',
    'NEW MEXICO': 'NM', 'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND', 'OHIO': 'OH',
    'OKLAHOMA': 'OK', 'OREGON': 'OR', 'PENNSYLVANIA': 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC',
    'SOUTH DAKOTA': 'SD', 'TENNESSEE': 'TN', 'TEXAS': 'TX', 'UTAH': 'UT', 'VERMONT': 'VT',
    'VIRGINIA': 'VA', 'WASHINGTON': 'WA', 'WEST VIRGINIA': 'WV', 'WISCONSIN': 'WI', 'WYOMING': 'WY'
  };
  
  const upperState = state.toUpperCase().trim();
  if (upperState.length === 2) return upperState;
  return stateMap[upperState] || state;
};

const formatDateTime = (dateString: string): string => {
  if (!dateString || dateString === '-') return '-';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return dateString;
  }
};

const formatDate = (dateString: string): string => {
  if (!dateString || dateString === '-') return '-';
  
  try {
    const date = new Date(dateString + 'T00:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    return dateString;
  }
};

const DeadDealsPage = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [realtors, setRealtors] = useState<Realtor[]>([]);
  const [compRates, setCompRates] = useState<CompRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [clientStateOptions, setClientStateOptions] = useState<string[]>([]);
  
  // Add user profile state
  const [userFullName, setUserFullName] = useState<string>('');
  
  // Add client info dialog state
  const [showClientInfoDialog, setShowClientInfoDialog] = useState(false);
  const [clientInfoDialogClient, setClientInfoDialogClient] = useState<Client | null>(null);
  
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
      } else {
        // Load user profile name
        const metadata = session.user.user_metadata;
        const firstName = metadata.first_name || '';
        const lastName = metadata.last_name || '';
        setUserFullName(`${firstName} ${lastName}`.trim() || 'User');
      }
    };
    checkAuth();
    
    fetchDeadDeals();
    loadRealtors();
    loadClientStates();
    loadCompRates();
  }, []);

  const loadCompRates = async () => {
    const { data, error } = await supabase
      .from('comp_rates')
      .select('*')
      .order('broker', { ascending: true });

    if (error) {
      console.error('Error loading comp rates:', error);
      return;
    }

    if (data) {
      setCompRates(data);
    }
  };

  const loadClientStates = async () => {
    const { data, error } = await supabase
      .from('client_states')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading client states:', error);
      return;
    }

    if (data && data.length > 0) {
      setClientStateOptions(data.map(s => s.state_name));
    } else {
      setClientStateOptions([
        'Lead Received; Not Yet Contacted',
        'Lead Received; Contacted; No Response',
        'Application Sent, Awaiting Completion',
        'Application Completed; Pre-Approval/VAL in Process',
        'Pre-Approved/VAL Complete; House Shopping',
        'Offer Accepted; Loan In Process',
        'Closed',
        'On Hold; Will Contact at Later Date',
        'Not Interested; Dead Deal'
      ]);
    }
  };

  const getPersonalCompPercentageOfTotal = (broker: string): number => {
    const rate = compRates.find(r => r.broker === broker);
    if (!rate) return 0;
    return (rate.comp_percentage * rate.personal_comp_percentage) / 100;
  };

  const calculateComp = (loanAmount: string, broker: string): number => {
    const amount = parsePurchasePrice(loanAmount);
    const personalCompPercentage = getPersonalCompPercentageOfTotal(broker);
    return (amount * personalCompPercentage) / 100;
  };

  const fetchDeadDeals = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('clients')
        .select('*')
        .eq('client_state', 'Not Interested; Dead Deal')
        .order('date_created', { ascending: false });

      if (fetchError) throw fetchError;
      
      setClients(data || []);
    } catch (err) {
      console.error('Error fetching dead deals:', err);
      setError('Failed to load dead deals');
    } finally {
      setLoading(false);
    }
  };

  const loadRealtors = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from('realtors')
      .select('*')
      .eq('user_id', session.user.id)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading realtors:', error);
      return;
    }

    if (data && data.length > 0) {
      setRealtors(data);
    }
  };

  const getLeadSourceDisplay = (client: Client): string => {
    if (client.lead_source === 'Realtor' && client.realtor_id) {
      const realtor = realtors.find(r => r.id === client.realtor_id);
      return realtor ? realtor.name : 'Realtor';
    }
    if (client.lead_source === 'FreeRateUpdate' || !client.realtor_id) {
      return 'FreeRateUpdate';
    }
    return client.lead_source || 'Realtor';
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
  };

  const handleSave = async (id: string) => {
    const client = clients.find(c => c.id === id);
    if (!client) return;

    const { error } = await supabase
      .from('clients')
      .update({
        name: client.name,
        email: client.email,
        phone: client.phone,
        state: client.state,
        purchase_price: client.purchase_price,
        broker: client.broker,
        last_contact: client.last_contact,
        client_state: client.client_state,
        loan_type: client.loan_type,
        loan_amount: client.loan_amount || null,
        lender_name: client.lender_name || null,
        comp: client.comp || null,
        interest_rate: client.interest_rate || null,
        notes: client.notes || null
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client');
      return;
    }

    setEditingId(null);
  };

  const handleChange = async (id: string, field: keyof Client, value: string) => {
    const client = clients.find(c => c.id === id);
    if (!client) return;

    if (field === 'client_state' && value !== client.client_state) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await supabase.from('client_state_history').insert({
          client_id: client.id,
          client_name: client.name,
          previous_state: client.client_state,
          new_state: value,
          user_id: session.user.id
        });
      }
    }

    setClients(clients.map(c => {
      if (c.id === id) {
        return { ...c, [field]: value };
      }
      return c;
    }));
  };

  const handleClientNameClick = (client: Client) => {
    setClientInfoDialogClient(client);
    setShowClientInfoDialog(true);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Filter clients by broker
  const filteredClients = clients;
  const nsClients = clients.filter(client => client.broker === 'NS');
  const tmClients = clients.filter(client => client.broker === 'TM');
  
  // Calculate totals
  const totalPurchasePrice = filteredClients.reduce((sum, client) => sum + parsePurchasePrice(client.purchase_price || '0'), 0);
  const totalLoanAmount = filteredClients.reduce((sum, client) => sum + parsePurchasePrice(client.loan_amount || '0'), 0);
  const totalComp = filteredClients.reduce((sum, client) => {
    if (client.comp) {
      return sum + parsePurchasePrice(client.comp);
    }
    if (client.loan_amount) {
      return sum + calculateComp(client.loan_amount, client.broker);
    }
    return sum;
  }, 0);

  const nsPurchasePrice = nsClients.reduce((sum, client) => sum + parsePurchasePrice(client.purchase_price || '0'), 0);
  const nsLoanAmount = nsClients.reduce((sum, client) => sum + parsePurchasePrice(client.loan_amount || '0'), 0);
  const nsComp = nsClients.reduce((sum, client) => {
    if (client.comp) {
      return sum + parsePurchasePrice(client.comp);
    }
    if (client.loan_amount) {
      return sum + calculateComp(client.loan_amount, client.broker);
    }
    return sum;
  }, 0);

  const tmPurchasePrice = tmClients.reduce((sum, client) => sum + parsePurchasePrice(client.purchase_price || '0'), 0);
  const tmLoanAmount = tmClients.reduce((sum, client) => sum + parsePurchasePrice(client.loan_amount || '0'), 0);
  const tmComp = tmClients.reduce((sum, client) => {
    if (client.comp) {
      return sum + parsePurchasePrice(client.comp);
    }
    if (client.loan_amount) {
      return sum + calculateComp(client.loan_amount, client.broker);
    }
    return sum;
  }, 0);

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center p-4">{error}</div>;
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      {/* Navigation */}
      <nav className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-home-heart-line text-3xl text-navy"></i>
            </div>
            <span className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-navy'}`}>{userFullName || 'Loans with Heena'}</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <a href="/" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-home-5-line mr-2"></i>
              Home
            </a>
            <a href="/crm" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-contacts-book-2-line mr-2"></i>
              CRM
            </a>
            
            {/* Pages Dropdown */}
            <div className="relative group">
              <button className={`${isDarkMode ? 'text-teal' : 'text-navy'} font-semibold whitespace-nowrap cursor-pointer flex items-center gap-1`}>
                <i className="ri-pages-line mr-2"></i>
                Pages
                <i className="ri-arrow-down-s-line"></i>
              </button>
              <div className={`absolute top-full left-0 mt-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-lg py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50`}>
                <a href="/closed" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-archive-line mr-2"></i>
                  Closed
                </a>
                <a href="/dead-deals" className={`block px-4 py-2 ${isDarkMode ? 'text-teal' : 'text-navy'} font-semibold cursor-pointer`}>
                  <i className="ri-close-circle-line mr-2"></i>
                  Dead Deals
                </a>
                <a href="/on-hold" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-pause-circle-line mr-2"></i>
                  On Hold
                </a>
                <a href="/templates" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-file-text-line mr-2"></i>
                  Templates
                </a>
                <a href="/intake" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-inbox-line mr-2"></i>
                  Message Intake
                </a>
                <a href="/history" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-history-line mr-2"></i>
                  History
                </a>
                <a href="/admin" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-settings-3-line mr-2"></i>
                  Admin
                </a>
              </div>
            </div>
            
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-xl`}></i>
            </button>
            
            <button
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-logout-box-line mr-2"></i>
              Logout
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:text-navy transition-colors cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}
          >
            <div className="px-6 py-4 space-y-3">
              <a href="/" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-home-5-line mr-2"></i>
                Home
              </a>
              <a href="/crm" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-contacts-book-2-line mr-2"></i>
                CRM
              </a>
              <a href="/closed" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-archive-line mr-2"></i>
                Closed
              </a>
              <a href="/dead-deals" className={`block py-2 ${isDarkMode ? 'text-teal' : 'text-navy'} font-semibold cursor-pointer`}>
                <i className="ri-close-circle-line mr-2"></i>
                Dead Deals
              </a>
              <a href="/on-hold" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-pause-circle-line mr-2"></i>
                On Hold
              </a>
              <a href="/templates" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-file-text-line mr-2"></i>
                Templates
              </a>
              <a href="/intake" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-inbox-line mr-2"></i>
                Message Intake
              </a>
              <a href="/history" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-history-line mr-2"></i>
                History
              </a>
              <a href="/admin" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-settings-3-line mr-2"></i>
                Admin
              </a>
              <button
                onClick={toggleDarkMode}
                className={`block w-full text-left py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}
              >
                <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} mr-2`}></i>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button
                onClick={handleLogout}
                className="block w-full text-left py-2 text-red-600 hover:text-red-700 transition-colors font-medium cursor-pointer"
              >
                <i className="ri-logout-box-line mr-2"></i>
                Logout
              </button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Main Content */}
      <div className="max-w-[1750px] mx-auto px-6 py-8">
        <div className="mb-8 text-center">
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'} mb-1`}>Dead Deals</h1>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>View all terminated client deals</p>
        </div>

        {/* Dead Deals Table */}
        <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border overflow-hidden mb-8`}>
          <div className="overflow-x-auto">
            {/* Desktop Table */}
            <table className="w-full hidden md:table" style={{ fontSize: '9.375px' }}>
              <thead className={`${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                <tr>
                  <th className={`px-2 py-2 text-left font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`} style={{ fontSize: '9.375px' }}>Client Name</th>
                  <th className={`px-2 py-2 text-left font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`} style={{ fontSize: '9.375px' }}>State</th>
                  <th className={`px-2 py-2 text-left font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`} style={{ fontSize: '9.375px' }}>Purchase Price</th>
                  <th className={`px-2 py-2 text-left font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`} style={{ fontSize: '9.375px' }}>Loan Amount</th>
                  <th className={`px-2 py-2 text-left font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`} style={{ fontSize: '9.375px' }}>Lead Source</th>
                  <th className={`px-2 py-2 text-left font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`} style={{ fontSize: '9.375px' }}>Broker Abbreviation</th>
                  <th className={`px-2 py-2 text-left font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`} style={{ fontSize: '9.375px' }}>Client State Updated</th>
                  <th className={`px-2 py-2 text-left font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`} style={{ fontSize: '9.375px' }}>Client State</th>
                  <th className={`px-2 py-2 text-left font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`} style={{ fontSize: '9.375px' }}>Last Contact</th>
                  <th className={`px-2 py-2 text-left font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`} style={{ fontSize: '9.375px' }}>Notes</th>
                  <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`} style={{ fontSize: '9.375px' }}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
                {filteredClients.map((client, index) => (
                  <tr key={client.id} className={`${isDarkMode ? (index % 2 === 0 ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-900 hover:bg-gray-700') : 'hover:bg-gray-50'} transition-colors`}>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => handleClientNameClick(client)}
                        className="font-medium text-teal hover:text-teal/80 underline cursor-pointer"
                        style={{ fontSize: '9.375px' }}
                      >
                        {client.name}
                      </button>
                    </td>
                    <td className="px-2 py-2">
                      {editingId === client.id ? (
                        <input
                          type="text"
                          value={client.state}
                          onChange={(e) => handleChange(client.id, 'state', e.target.value)}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal`}
                          style={{ fontSize: '9.375px' }}
                        />
                      ) : (
                        <span className={`${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{getStateAbbreviation(client.state) || '-'}</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {editingId === client.id ? (
                        <input
                          type="text"
                          value={client.purchase_price}
                          onChange={(e) => handleChange(client.id, 'purchase_price', e.target.value)}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal`}
                          style={{ fontSize: '9.375px' }}
                        />
                      ) : (
                        <span className={`font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>{formatPurchasePrice(client.purchase_price) || '-'}</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {editingId === client.id ? (
                        <input
                          type="text"
                          value={client.loan_amount || ''}
                          onChange={(e) => handleChange(client.id, 'loan_amount', e.target.value)}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal`}
                          style={{ fontSize: '9.375px' }}
                        />
                      ) : (
                        <span className={`font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>{client.loan_amount ? formatPurchasePrice(client.loan_amount) : '-'}</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <span className={`${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{getLeadSourceDisplay(client)}</span>
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full font-medium ${
                        client.broker === 'NS' 
                          ? 'bg-teal/10 text-teal' 
                          : 'bg-orange-100 text-orange-700'
                      }`} style={{ fontSize: '9.375px' }}>
                        {client.broker || '-'}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <span className={`${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{formatDateTime(client.client_state_updated || '-')}</span>
                    </td>
                    <td className="px-2 py-2">
                      {editingId === client.id ? (
                        <select
                          value={client.client_state}
                          onChange={(e) => handleChange(client.id, 'client_state', e.target.value)}
                          className={`px-1.5 py-0.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal cursor-pointer`}
                          style={{ fontSize: '9.375px' }}
                        >
                          {clientStateOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'} whitespace-nowrap`} style={{ fontSize: '9.375px' }}>
                          {client.client_state || 'Not Interested; Dead Deal'}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <span className={`${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{formatDateTime(client.last_contact || '-')}</span>
                    </td>
                    <td className="px-2 py-2">
                      {editingId === client.id ? (
                        <textarea
                          value={client.notes || ''}
                          onChange={(e) => handleChange(client.id, 'notes', e.target.value)}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal`}
                          style={{ fontSize: '9.375px' }}
                          rows={2}
                        />
                      ) : (
                        <span className={`${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{client.notes || '-'}</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-center gap-1">
                        {editingId === client.id ? (
                          <>
                            <button
                              onClick={() => handleSave(client.id)}
                              className="w-5 h-5 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                              title="Save"
                            >
                              <i className="ri-check-line text-xs"></i>
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className={`w-5 h-5 flex items-center justify-center ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'} rounded-lg hover:bg-gray-200 transition-colors cursor-pointer`}
                              title="Cancel"
                            >
                              <i className="ri-close-line text-xs"></i>
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleEdit(client.id)}
                            className="w-5 h-5 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer"
                            title="Edit"
                          >
                            <i className="ri-edit-line text-xs"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {filteredClients.map((client, index) => (
                <div key={client.id} className={`p-4 ${isDarkMode ? (index % 2 === 0 ? 'bg-[#212936]' : 'bg-[#121826]') : 'hover:bg-gray-50'} transition-colors`}>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      {editingId === client.id ? (
                        <input
                          type="text"
                          value={client.name}
                          onChange={(e) => handleChange(client.id, 'name', e.target.value)}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                      ) : (
                        <div className={`font-semibold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'} text-sm truncate`}>{client.name || '-'}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium text-xs ${
                        client.broker === 'NS' 
                          ? 'bg-teal/10 text-teal' 
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {client.broker || '-'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className={`text-xs ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-500'} mb-0.5`}>Loan Amount</div>
                      {editingId === client.id ? (
                        <input
                          type="text"
                          value={client.loan_amount || ''}
                          onChange={(e) => handleChange(client.id, 'loan_amount', e.target.value)}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                      ) : (
                        <div className={`font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'} text-sm`}>
                          {client.loan_amount ? formatPurchasePrice(client.loan_amount) : '-'}
                        </div>
                      )}
                    </div>
                  </div>

                  {client.notes && (
                    <div className="mb-3">
                      <div className={`text-xs ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-500'} mb-0.5`}>Notes</div>
                      {editingId === client.id ? (
                        <textarea
                          value={client.notes || ''}
                          onChange={(e) => handleChange(client.id, 'notes', e.target.value)}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                          rows={2}
                        />
                      ) : (
                        <div className={`text-sm ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`}>{client.notes}</div>
                      )}
                    </div>
                  )}

                  <div className={`flex items-center justify-end gap-2 pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    {editingId === client.id ? (
                      <>
                        <button
                          onClick={() => handleSave(client.id)}
                          className="px-3 py-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer text-xs font-medium"
                        >
                          <i className="ri-check-line mr-1"></i>
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className={`px-3 py-1.5 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'} rounded-lg hover:bg-gray-200 transition-colors cursor-pointer text-xs font-medium`}
                        >
                          <i className="ri-close-line mr-1"></i>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleEdit(client.id)}
                        className="px-3 py-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer text-xs font-medium"
                      >
                        <i className="ri-edit-line mr-1"></i>
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {filteredClients.length === 0 && (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No dead deals found
              </div>
            )}
          </div>
        </div>

        {/* Summary Boxes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Total Dead Deals */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg">
                <i className="ri-close-circle-line text-2xl text-red-600"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Dead Deals</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Clients:</span>
                <span className="text-2xl font-bold text-gray-900">{filteredClients.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Purchase Price Total:</span>
                <span className="text-xl font-bold text-red-600">{formatCurrency(totalPurchasePrice)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Loan Amount Total:</span>
                <span className="text-xl font-bold text-red-600">{formatCurrency(totalLoanAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Comp Total:</span>
                <span className="text-xl font-bold text-red-600">{formatCurrency(totalComp)}</span>
              </div>
            </div>
          </motion.div>

          {/* NS (Next Step Lending) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-black rounded-xl shadow-sm border border-gray-800 p-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div>
                <p className="text-sm text-[#54a7dc] font-medium">NS (Next Step Lending)</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Clients:</span>
                <span className="text-2xl font-bold text-white">{nsClients.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Purchase Price Total:</span>
                <span className="text-xl font-bold text-[#54a7dc]">{formatCurrency(nsPurchasePrice)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Loan Amount Total:</span>
                <span className="text-xl font-bold text-[#54a7dc]">{formatCurrency(nsLoanAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Comp Total:</span>
                <span className="text-xl font-bold text-[#54a7dc]">{formatCurrency(nsComp)}</span>
              </div>
            </div>
          </motion.div>

          {/* TM (Trademark Financing) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#f3e8c5] rounded-xl shadow-sm border border-[#e5d9b0] p-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div>
                <p className="text-sm text-[#1c272b] font-medium">TM (Trademark Financing)</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5a5a5a]">Clients:</span>
                <span className="text-2xl font-bold text-[#1c272b]">{tmClients.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5a5a5a]">Purchase Price Total:</span>
                <span className="text-xl font-bold text-[#1c272b]">{formatCurrency(tmPurchasePrice)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5a5a5a]">Loan Amount Total:</span>
                <span className="text-xl font-bold text-[#1c272b]">{formatCurrency(tmLoanAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5a5a5a]">Comp Total:</span>
                <span className="text-xl font-bold text-[#1c272b]">{formatCurrency(tmComp)}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Client Info Dialog */}
      {showClientInfoDialog && clientInfoDialogClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Client Information</h3>
                <button
                  onClick={() => setShowClientInfoDialog(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">{clientInfoDialogClient.name}</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <a 
                    href={`mailto:${clientInfoDialogClient.email}`}
                    className="text-sm text-teal hover:text-teal/80 font-medium cursor-pointer underline"
                  >
                    {clientInfoDialogClient.email}
                  </a>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <a 
                    href={`tel:${clientInfoDialogClient.phone.replace(/\D/g, '')}`}
                    className="text-sm text-teal hover:text-teal/80 font-medium cursor-pointer underline"
                  >
                    {formatPhoneNumber(clientInfoDialogClient.phone)}
                  </a>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <p className="text-sm text-gray-900">{getStateAbbreviation(clientInfoDialogClient.state)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Price</label>
                  <p className="text-sm text-gray-900">{formatPurchasePrice(clientInfoDialogClient.purchase_price)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Loan Amount</label>
                  <p className="text-sm text-gray-900">{clientInfoDialogClient.loan_amount ? formatPurchasePrice(clientInfoDialogClient.loan_amount) : '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Interest Rate</label>
                  <p className="text-sm text-gray-900">{clientInfoDialogClient.interest_rate ? `${clientInfoDialogClient.interest_rate}%` : '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Loan Type</label>
                  <p className="text-sm text-gray-900">{clientInfoDialogClient.loan_type || '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lead Source</label>
                  <p className="text-sm text-gray-900">{getLeadSourceDisplay(clientInfoDialogClient)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lender Name</label>
                  <p className="text-sm text-gray-900">{clientInfoDialogClient.lender_name || '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Broker</label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full font-medium text-sm ${
                    clientInfoDialogClient.broker === 'NS' 
                      ? 'bg-teal/10 text-teal' 
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {clientInfoDialogClient.broker}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Comp</label>
                  <p className="text-sm font-semibold text-teal">
                    {clientInfoDialogClient.comp 
                      ? formatPurchasePrice(clientInfoDialogClient.comp) 
                      : clientInfoDialogClient.loan_amount 
                        ? formatCurrency(calculateComp(clientInfoDialogClient.loan_amount, clientInfoDialogClient.broker))
                        : '-'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Contact</label>
                  <p className="text-sm text-gray-900">{formatDateTime(clientInfoDialogClient.last_contact || '-')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client State Updated</label>
                  <p className="text-sm text-gray-900">{formatDateTime(clientInfoDialogClient.client_state_updated || '-')}</p>
                </div>

                {clientInfoDialogClient.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <p className="text-sm text-gray-900">{clientInfoDialogClient.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DeadDealsPage;
